import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, Wrench, Clock, CheckCircle, XCircle, 
  AlertTriangle, Play, Pause, ChevronRight, Calendar, User, Truck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const WorkOrders = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState({
    machinery_id: '',
    description: '',
    priority: 'normal',
    work_type: 'corrective',
    supervisor_id: '',
    mechanic_id: '',
    helper_id: '',
    estimated_hours: '',
    scheduled_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const [ordersRes, machineryRes, usersRes, summaryRes] = await Promise.all([
        axios.get(`${API}/work-orders${params}`),
        axios.get(`${API}/machinery`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/work-orders/stats/summary`)
      ]);
      setOrders(ordersRes.data);
      setMachinery(machineryRes.data);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Error al cargar órdenes de trabajo');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null
      };
      await axios.post(`${API}/work-orders`, payload);
      toast.success('Orden de trabajo creada');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear orden');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/work-orders/${orderId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      machinery_id: '',
      description: '',
      priority: 'normal',
      work_type: 'corrective',
      supervisor_id: '',
      mechanic_id: '',
      helper_id: '',
      estimated_hours: '',
      scheduled_date: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-700', label: 'En Proceso', icon: Play },
      completed: { color: 'bg-green-100 text-green-700', label: 'Finalizada', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelada', icon: XCircle }
    };
    const { color, label, icon: Icon } = config[status] || config.pending;
    return (
      <span className={`status-badge ${color} flex items-center`}>
        <Icon size={14} className="mr-1" />
        {label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { color: 'bg-slate-100 text-slate-700', label: 'Baja' },
      normal: { color: 'bg-blue-100 text-blue-700', label: 'Normal' },
      high: { color: 'bg-orange-100 text-orange-700', label: 'Alta' },
      urgent: { color: 'bg-red-100 text-red-700', label: 'Urgente' }
    };
    const { color, label } = config[priority] || config.normal;
    return <span className={`status-badge ${color}`}>{label}</span>;
  };

  const getWorkTypeBadge = (type) => {
    const config = {
      preventive: { color: 'bg-green-100 text-green-700', label: 'Preventivo' },
      corrective: { color: 'bg-orange-100 text-orange-700', label: 'Correctivo' },
      predictive: { color: 'bg-purple-100 text-purple-700', label: 'Predictivo' }
    };
    const { color, label } = config[type] || config.corrective;
    return <span className={`status-badge ${color}`}>{label}</span>;
  };

  const filteredOrders = orders.filter(order =>
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.machinery_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="work-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Órdenes de Trabajo
          </h1>
          <p className="text-slate-600 mt-2">Gestión de mantenimientos y reparaciones</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-work-order-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nueva OT
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nueva Orden de Trabajo
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Maquinaria *</label>
                <select
                  value={formData.machinery_id}
                  onChange={(e) => setFormData({ ...formData, machinery_id: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Seleccionar maquinaria...</option>
                  {machinery.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} - {m.code}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción del Trabajo *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder="Describa el trabajo a realizar..."
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Prioridad</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="input-field"
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
                  <select
                    value={formData.work_type}
                    onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
                    className="input-field"
                  >
                    <option value="preventive">Preventivo</option>
                    <option value="corrective">Correctivo</option>
                    <option value="predictive">Predictivo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Horas Estimadas</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    className="input-field"
                    placeholder="Ej: 4"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Supervisor</label>
                  <select
                    value={formData.supervisor_id}
                    onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.filter(u => ['admin', 'supervisor'].includes(u.role)).map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mecánico</label>
                  <select
                    value={formData.mechanic_id}
                    onChange={(e) => setFormData({ ...formData, mechanic_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Ayudante</label>
                  <select
                    value={formData.helper_id}
                    onChange={(e) => setFormData({ ...formData, helper_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Seleccionar...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha Programada</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field"
                  rows={2}
                  placeholder="Observaciones adicionales..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" onClick={() => { setDialogOpen(false); resetForm(); }} className="btn-secondary">
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary">
                  Crear Orden
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-700">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-800">{summary.pending?.count || 0}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-700">En Proceso</p>
            <p className="text-2xl font-bold text-blue-800">{summary.in_progress?.count || 0}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-700">Finalizadas</p>
            <p className="text-2xl font-bold text-green-800">{summary.completed?.count || 0}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">Canceladas</p>
            <p className="text-2xl font-bold text-red-800">{summary.cancelled?.count || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <p className="text-sm text-slate-700">Costo Total</p>
            <p className="text-2xl font-bold text-slate-800 mono">Bs. {(summary.total?.cost || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex space-x-2">
          {['all', 'pending', 'in_progress', 'completed', 'cancelled'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f === 'all' ? 'Todas' : 
               f === 'pending' ? 'Pendientes' : 
               f === 'in_progress' ? 'En Proceso' : 
               f === 'completed' ? 'Finalizadas' : 'Canceladas'}
            </button>
          ))}
        </div>
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por número, máquina o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/work-orders/${order.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-lg font-bold text-orange-600 mono">{order.order_number}</span>
                    {getStatusBadge(order.status)}
                    {getPriorityBadge(order.priority)}
                    {getWorkTypeBadge(order.work_type)}
                  </div>
                  <p className="text-slate-800 font-medium mb-1">{order.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-slate-600">
                    <span className="flex items-center">
                      <Truck size={14} className="mr-1" />
                      {order.machinery_name || 'Sin asignar'}
                    </span>
                    {order.mechanic_name && (
                      <span className="flex items-center">
                        <User size={14} className="mr-1" />
                        {order.mechanic_name}
                      </span>
                    )}
                    {order.scheduled_date && (
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {new Date(order.scheduled_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {order.status === 'pending' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStatusChange(order.id, 'in_progress'); }}
                      className="p-2 bg-blue-100 hover:bg-blue-200 rounded text-blue-700"
                      title="Iniciar"
                    >
                      <Play size={18} />
                    </button>
                  )}
                  {order.status === 'in_progress' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStatusChange(order.id, 'completed'); }}
                      className="p-2 bg-green-100 hover:bg-green-200 rounded text-green-700"
                      title="Finalizar"
                    >
                      <CheckCircle size={18} />
                    </button>
                  )}
                  <ChevronRight size={20} className="text-slate-400" />
                </div>
              </div>
              {order.total_cost > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-600">Costo Total:</span>
                  <span className="font-bold mono text-orange-600">Bs. {order.total_cost.toFixed(2)}</span>
                </div>
              )}
            </div>
          ))}

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Wrench size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No hay órdenes de trabajo</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrders;
